import {createSlice, Dispatch} from '@reduxjs/toolkit';
import {AuthService, CustomersService} from "../actions";
import {toast} from "react-hot-toast";
import {parseJwt} from "../utils";
import agencyService from '../actions/agency.service';

enum AuthenticationDrawerContent {
  Login = "login",
  Register = "register",
  ForgotPassword = 'forgot-password'
}

export const setLoginDrawer = (body: any) => async (dispatch: Dispatch) => {
  dispatch(loginDrawer(body));
}

export const setDrawerContent = (body: any) => async (dispatch: Dispatch) => {
  dispatch(drawerContent(body));
}

export const logout = () => async (dispatch: Dispatch) => {
  dispatch(logoutUser());
  console.log("You are logged out!");
}

export const auth = (body: any, isFromRegister?: boolean, onSuccess?: () => void): any => async (dispatch: Dispatch) => {
  dispatch(setLoading(true))
  try {
    const response = await AuthService.login(body);

    const jwtTokenData = parseJwt(response.access_token);
    const authorities = jwtTokenData?.authorities;
    if (authorities && authorities.length) {
      if (authorities.includes("ROLE_ADMIN") || authorities.includes("ROLE_AGENT")) {
        dispatch(setErrorMsg('You are not allowed to log in'))
        dispatch(setLoading(false))
        return;
      }
    }

    if (!isFromRegister) {
      console.log("You are logged in!");
    }
    dispatch(loginUser(response));
    dispatch(loginDrawer(false));
    dispatch(setLoading(false))
    dispatch(setErrorMsg(''))
    onSuccess && onSuccess();

    return response;
  } catch (err: any) {
    const backendMsg = err.message || 'Login failed';
    dispatch(setErrorMsg(backendMsg));
    dispatch(setLoading(false));
    return { error: backendMsg };
  }
}

export const forgotPassword = (email: any, onSuccess?: () => void): any => async (dispatch: Dispatch) => {
  dispatch(setLoading(true))
  try {
    const response = await AuthService.forgotPassword(email);
    console.log("Successfully!");
    onSuccess && onSuccess();
    dispatch(setLoading(false))
    return response;
  } catch (err) {
    dispatch(setLoading(false))
    console.log(err);
    throw err;
  }
}

export const resetAccountPassword = (
  body: { newPassword: string; confirmNewPassword: string },
  token: string,
  onSuccess?: () => void
): any => async (dispatch: Dispatch) => {
  dispatch(setLoading(true))
  try {
    const response = await AuthService.resetPassword(body, token);
    console.log("You changed password successfully!");
    onSuccess && onSuccess();
    dispatch(setLoading(false))
    return response;
  } catch (err) {
    dispatch(setLoading(false))
    console.log(err);
    throw err;
  }
}

export const refreshAuth = (body: any): any => async (dispatch: Dispatch) => {
  try {
    const responseRfsh = await AuthService.rfshToken(body);
    console.log(responseRfsh);
    dispatch(reAuth(responseRfsh));
    return responseRfsh;
  } catch (err) {
    console.log(err);
    throw err;
  }
}

export const fetchAccount = (): any => async (dispatch: Dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await AuthService.getAccount();
    dispatch(setLoading(false));
    return response;
  } catch (err: any) {
    dispatch(setErrorMsg(err.message || 'Failed to fetch account data'));
    dispatch(setLoading(false));
    throw err;
  }
}

export const updateAccount = (body: any, onSuccess?: () => void): any => async (dispatch: Dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await AuthService.updateAccount(body);
    console.log("Account updated successfully!");
    dispatch(updateUserProfile({
      fullName: response?.fullName ?? body?.fullName,
      email: response?.email ?? body?.email,
    }));
    dispatch(setLoading(false));
    dispatch(setErrorMsg(''));
    onSuccess && onSuccess();
    return response;
  } catch (err: any) {
    const backendMsg = err.message || 'Failed to update account';
    dispatch(setErrorMsg(backendMsg));
    dispatch(setLoading(false));
    throw err;
  }
}


export const submitAgencyApplication = (
  formData: any,
  files: File[],
  onSuccess?: () => void
): any => async (dispatch: Dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await agencyService.submitAgencyApplication(formData, files);

    // toast.success("Application submitted successfully!");
    dispatch(setErrorMsg(""));
    dispatch(setLoading(false));

    if (onSuccess) {
      onSuccess();
    }

    return response;
  } catch (error: any) {
    dispatch(setLoading(false));

    let errorMessage = "Failed to submit application. Please try again.";
    if (error.response) {
      if (error.response.status === 400) {
        errorMessage = "Invalid application data. Please check your inputs.";
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    dispatch(setErrorMsg(errorMessage));
    toast.error(errorMessage);
    throw error;
  }
};

export const register = (body: any, onSuccess: () => void) => async (dispatch: Dispatch) => {
  dispatch(setLoading(true));
  try {
    dispatch(auth(body, true));
    console.log("You are registered!");
    onSuccess();
  } catch (err: any) {
    dispatch(setErrorMsg(err?.data?.message || 'Registration failed'));
    dispatch(setLoading(false))
  }
}

interface LoginSlice {
  isDrawerOpen: boolean;
  drawerContent: AuthenticationDrawerContent;
  forgotPasswordModal: boolean;
  userFullName: string | null;
  userEmail: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  errorMsg: string;
}

const initialState: LoginSlice = {
  isDrawerOpen: false,
  drawerContent: AuthenticationDrawerContent.Login,
  accessToken: localStorage.getItem("access_token"),
  refreshToken: localStorage.getItem("refresh_token"),
  userFullName: localStorage.getItem("user_name"),
  userEmail: localStorage.getItem("user_email"),
  forgotPasswordModal: false,
  loading: false,
  errorMsg: ''
}

const stateSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    loginDrawer: (state, action) => {
      state.isDrawerOpen = action.payload
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setErrorMsg: (state, action) => {
      state.errorMsg = action.payload
    },
    loginUser: (state, action) => {
      const accessToken = action.payload.access_token;
      const refreshToken = action.payload.refresh_token;
      const name = action.payload.fullName;
      const email = action.payload.email;

      if (accessToken) localStorage.setItem("access_token", accessToken);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
      if (name) localStorage.setItem("user_name", name);
      if (email) localStorage.setItem("user_email", email);

      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.userFullName = name;
      state.userEmail = email;
    },
    reAuth: (state, action) => {
      const accessToken = action.payload.access_token;
      const refreshToken = action.payload.refresh_token;
      const name = action.payload.fullName;
      const email = action.payload.email;

      if (accessToken) localStorage.setItem("access_token", accessToken);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
      if (name) localStorage.setItem("user_name", name);
      if (email) localStorage.setItem("user_email", email);

      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.userFullName = name;
      state.userEmail = email;
    },
    updateUserProfile: (state, action) => {
      const name = action.payload?.fullName;
      const email = action.payload?.email;

      if (typeof name === "string") {
        if (name.trim()) {
          localStorage.setItem("user_name", name);
          state.userFullName = name;
        } else {
          localStorage.removeItem("user_name");
          state.userFullName = null;
        }
      }

      if (typeof email === "string") {
        if (email.trim()) {
          localStorage.setItem("user_email", email);
          state.userEmail = email;
        } else {
          localStorage.removeItem("user_email");
          state.userEmail = null;
        }
      }
    },
    logoutUser: (state) => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_name");
      localStorage.removeItem("user_email");
      state.accessToken = null
      state.refreshToken = null
      state.userFullName = null
      state.userEmail = null
    },
    drawerContent: (state, action) => {
      state.drawerContent = action.payload
    },
    forgotPasswordModal: (state, action) => {
      state.forgotPasswordModal = action.payload
    },
  }
});

export const {
  loginDrawer,
  loginUser,
  logoutUser,
  drawerContent,
  forgotPasswordModal,
  reAuth,
  updateUserProfile,
  setLoading,
  setErrorMsg
} = stateSlice.actions;

export default stateSlice.reducer;
